import { ICalendarHeader, ICalendarItemData } from 'Calendar/types';
import get from 'lodash/get';
import React, { memo, useMemo } from 'react';
import { styled } from 'styled-components';
import Appointment from './Appointment';
import MultiAppointment from './MultiAppointment';
import BlockHourItem from './BlockHour';
import { Popover } from 'antd';
import Icon from 'assets/Icons';
import { calendarDOM } from 'Calendar/styles';
import { CALENDAR_VIEW_TYPE } from 'constants/calendar';
import { calendarSelectors } from 'services/selectors/apt';
import SplitBlockColumn from './SplitBlockColumn';
import { CellRange } from './multi-layout';
type ICalendarItemProps = {
  data: ICalendarItemData[];
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  disabled?: boolean;
  /** Day view: time window of the merged cell, used to place items inside a split cell. */
  cellRange?: CellRange;
};
const CalendarItem: React.FC<ICalendarItemProps> = ({ disabled, data, headerData, isMonthView, dateOfMonth, cellRange }) => {
  const { aptList, blockHoursList } = useMemo(() => ({
    aptList: data.filter(o => o.type === 'apt'),
    blockHoursList: data.filter(o => o.type === 'block-hour'),
  }), [data]);

  // Splitting a merged cell into side-by-side items only reads in day view, where a column is one
  // staff and a row is a time slot. Week and month columns are dates and keep the summary
  // renderings below. Single day-view gate for both the grid and the block/appointment split.
  const split = calendarSelectors.typeView() === CALENDAR_VIEW_TYPE.DAY_VIEW;

  const contentBreakTime = useMemo(() => {
    if (!aptList?.length) return null;
    return (
      <ContentStyled>
        {(aptList.length > 1 ?
          <MultiAppointment
            headerData={headerData}
            data={aptList}
            isMonthView={isMonthView}
            dateOfMonth={dateOfMonth}
            disabled={disabled}
          /> : <Appointment
            headerData={headerData}
            data={get(aptList, [0])}
            isMonthView={isMonthView}
            dateOfMonth={dateOfMonth}
            disabled={disabled}
          />)}
      </ContentStyled>
    );
  }, [aptList, headerData, isMonthView, dateOfMonth, disabled]);

  // Day view: blocks and appointments sharing a merged cell are laid out side by side, each at its
  // own time offset. Week view falls through to the block + "!" popover below, where a column is a
  // date and a split would have nothing to position against.
  if (split && cellRange && blockHoursList.length && aptList.length) {
    return (
      <CalendarItemStyled isMonthView={isMonthView}>
        <SplitBlockColumn
          blockItems={blockHoursList}
          appointments={aptList}
          cellRange={cellRange}
          headerData={headerData}
          isMonthView={isMonthView}
          disabled={disabled}
        />
      </CalendarItemStyled>
    );
  }

  if (blockHoursList.length && aptList.length) {
    return (
      <CalendarItemStyled isMonthView={isMonthView}>
        <div className="top">
          <Popover content={contentBreakTime} trigger={['click']}
            // @ts-ignore 
            getTooltipContainer={() => calendarDOM.current}>
            <button style={{ height: '32px', width: '32px' }}><Icon type='exclamationCircle' /></button>
          </Popover>
        </div>
        <BlockHourItem
          headerData={headerData}
          data={get(blockHoursList, [0])}
          isMonthView={isMonthView}
          dateOfMonth={dateOfMonth}
          disabled={disabled}
        />

      </CalendarItemStyled>
    );
  }

  if (blockHoursList.length) {
    return (
      <CalendarItemStyled isMonthView={isMonthView}>
        <BlockHourItem
          headerData={headerData}
          data={get(blockHoursList, [0])}
          isMonthView={isMonthView}
          dateOfMonth={dateOfMonth}
          disabled={disabled}
        />
      </CalendarItemStyled>
    );
  }

  if (aptList.length)
    return (
      <CalendarItemStyled isMonthView={isMonthView}>
        {(aptList.length > 1 ?
          <MultiAppointment
            headerData={headerData}
            data={aptList}
            isMonthView={isMonthView}
            dateOfMonth={dateOfMonth}
            disabled={disabled}
            split={split}
            cellRange={cellRange}
          /> : <Appointment
            headerData={headerData}
            data={get(aptList, [0])}
            isMonthView={isMonthView}
            dateOfMonth={dateOfMonth}
            disabled={disabled}
          />)}
      </CalendarItemStyled>
    );
  return null;
};

export default memo(CalendarItem);
type CalendarItemStyledProps = {
  isMonthView?: boolean;
};
const CalendarItemStyled = styled.div<CalendarItemStyledProps>`
  height: 100%;
  width: 100%;
  position:relative;
  .top {
    position: absolute;
    top: -15px;
    right:0px;
    z-index: 1;
    svg {
      transform: scale(1.3);
    }
  }
`;

const ContentStyled = styled.div`
  display:flex;
  flex-direction: column;
  gap:12px;
  height: 5rem;
  width: 15rem;
  margin-bottom: 1rem;
`;